import { inject, Injectable, signal } from '@angular/core';

import { ConfigService, StorageService } from '@venecia/services';
import { GlobalConstants } from '@venecia/constants';
import { take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

export interface Language {
    readonly default?: boolean;
    readonly code: string;
    readonly label: string;
}

@Injectable({ providedIn: 'root' })
export class LanguageService {

    private configService = inject(ConfigService);
    private readonly storageService = inject(StorageService);
    private readonly supportedLanguages = signal<Language[]>([]);
    private readonly currentLanguage = signal<string | undefined>(undefined);

    readonly currentLanguage$ = toObservable(this.currentLanguage);

    constructor() {
        this.configService
            .select<readonly Language[]>('languages', [])
            .subscribe((languages) => {
                this.supportedLanguages.set(languages);
            });
    }

    getCurrentOrDefault(): string {
        const storedLang = this.storageService.get<string>(GlobalConstants.LANG_KEY);
        if (this.isSupportedLanguage(storedLang)) {
            return storedLang!;
        }

        return this.getDefault();
    }

    setCurrent(langCode: string): void {
        this.storageService.set<string>(GlobalConstants.LANG_KEY, langCode);
        this.currentLanguage.set(langCode);
    }

    getDefault(): string {
        return this.supportedLanguages().find((language) => language.default)?.code || 'es';
    }

    getSupportedLanguages(): Language[] {
        return this.supportedLanguages();
    }

    getSupportedCodeLanguage(): string[] {
        return this.supportedLanguages().map(language => language.code);
    }

    isSupportedLanguage(langCode?: string | null): boolean {
        return typeof langCode === 'string' && this.supportedLanguages().some((language) => language.code === langCode);
    }
}
